import React from "react";
import WorkspaceSelector from "@/components/WorkspaceSelector";
import ThemeToggle from "@/components/ThemeToggle";
import UserMenu from "@/components/UserMenu";
import { Bell, LayoutGrid } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { Link, useLocation } from "react-router-dom";

/**
 * Premium SaaS-style header.
 *
 * Layout: three sections on a single row (Brand · Workspace · Actions).
 * Height ~64px desktop, subtle bottom hairline, sticky, no extra panel row
 * underneath.  Workspace selector shrinks gracefully on smaller screens
 * and stays inside the main row on mobile (no dedicated mobile bar).
 */
export default function AppShell({ children, showHubLink = false }) {
  const loc = useLocation();
  const onHub = loc.pathname === "/" || loc.pathname === "/hub";

  return (
    <div className="h-screen w-full flex flex-col surface overflow-hidden">
      <header
        data-testid="top-nav"
        className="sticky top-0 z-40 h-16 shrink-0 border-b hairline bg-background/85 backdrop-blur-md flex items-center gap-4 sm:gap-6 px-4 sm:px-6"
      >
        {/* LEFT — brand + optional back-to-hub */}
        <div className="flex items-center gap-3 shrink-0 min-w-0">
          <Link
            to="/"
            data-testid="brand-home-link"
            className="flex items-center gap-2.5 shrink-0 rounded-[10px] px-1 -mx-1 py-1 hover:bg-foreground/[0.05] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
          >
            <span className="relative h-8 w-8 rounded-[9px] grid place-items-center bg-gradient-to-br from-primary/95 to-secondary/95">
              <span className="text-white font-black text-[15px] leading-none tracking-tight">O</span>
            </span>
            <span className="hidden sm:flex flex-col leading-tight">
              <span className="text-[15px] font-semibold ink tracking-tight">Project Owl</span>
              <span className="text-[10px] ink-muted -mt-[1px]">Operations System</span>
            </span>
          </Link>

          {!onHub && showHubLink && (
            <Link
              to="/"
              data-testid="back-to-hub-btn"
              className="hidden md:inline-flex items-center gap-1.5 h-[34px] pl-2 pr-2.5 rounded-[9px] ink-muted hover:ink hover:bg-foreground/[0.06] text-[12.5px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
              title="Back to all modules"
            >
              <LayoutGrid size={13} />
              <span>Modules</span>
            </Link>
          )}
        </div>

        {/* CENTER — unified workspace selector */}
        <div className="flex-1 flex items-center justify-center min-w-0">
          <div className="w-full max-w-[440px] min-w-0">
            <WorkspaceSelector />
          </div>
        </div>

        {/* RIGHT — actions */}
        <div className="flex items-center gap-1.5 shrink-0">
          <ThemeToggle />
          <button
            type="button"
            data-testid="notifications-btn"
            aria-label="Notifications"
            title="Notifications"
            className="h-[38px] w-[38px] grid place-items-center rounded-[10px] ink-muted hover:ink hover:bg-foreground/[0.06] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
          >
            <Bell size={16} />
          </button>
          <div className="mx-1 hidden sm:block h-6 w-px bg-border/60" aria-hidden />
          <UserMenu />
        </div>
      </header>

      <main className="flex-1 overflow-hidden surface" data-testid="app-main">
        {children}
      </main>
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}
